import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { env } from '@/utils/env';
import { getAccessToken, getCurrentId } from '@/services';
import { getSessionId } from '@/services/correlation';
import { API_HEADERS, API_CONTENT_TYPES, API_TIMEOUT_MS } from '@/constants';
import { createAuthTokenError } from './authTokenError';

const rawBaseQuery = fetchBaseQuery({
  baseUrl: env().apiBaseUrl,
  timeout: API_TIMEOUT_MS,
  prepareHeaders: (headers) => {
    if (!headers.has(API_HEADERS.CONTENT_TYPE)) {
      headers.set(API_HEADERS.CONTENT_TYPE, API_CONTENT_TYPES.JSON);
    }
    headers.set(API_HEADERS.CORRELATION_ID, getCurrentId());
    headers.set('X-Session-Id', getSessionId());
    return headers;
  },
});

/**
 * Every API request must carry a bearer token. If one cannot be acquired the request is not
 * sent (instead of going out unauthenticated); a caller-supplied Authorization header, such
 * as the employee-search retry with a force-refreshed token, is respected.
 */
const baseQueryWithAuth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  const request: FetchArgs = typeof args === 'string' ? { url: args } : args;
  const headers = new Headers(request.headers as HeadersInit | undefined);

  if (!headers.has(API_HEADERS.AUTHORIZATION)) {
    const token = await getAccessToken();
    if (!token) {
      return { error: createAuthTokenError() };
    }
    headers.set(API_HEADERS.AUTHORIZATION, `Bearer ${token}`);
  }

  return rawBaseQuery({ ...request, headers }, api, extraOptions);
};

export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'Events',
    'EventDetail',
    'EventMetrics',
    'Spaces',
    'Buildings',
    'Regions',
    'PlannerAccess',
  ],
  endpoints: () => ({}),
});
